/* --------------------------------------------------------------------------
    PREMIUM AGENCY HEADER (SEO OPTIMIZED)
    Mobile Logo Increased | Mobile Visibility Fix
--------------------------------------------------------------------------- */

import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronRight } from "lucide-react";
import { Helmet } from "react-helmet"; // Added for Schema

const Header = () => {
  // --- THEME CONFIG ---
  const THEME = {
    textMain: "text-[#0F0F0F]",
    bgScrolled: "bg-[#F3F2ED]/95", 
    menuBg: "bg-[#0F0F0F]",
    menuText: "text-[#F3F2ED]",
  };

  // --- LOGO SIZING (UPDATED: BIGGER ON MOBILE) ---
  const logoSizeDefault = "h-40 sm:h-48 md:h-56 lg:h-64";
  const logoSizeScrolled = "h-40 sm:h-48 md:h-56 lg:h-64"; 

  // --- LOGIC & STATE ---
  const menuRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);

  const location = useLocation();
  const navigate = useNavigate();

  // Scroll Detection
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Resize Detection
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Click Outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ESC Key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isMenuOpen) setIsMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isMenuOpen]);

  // Navigation Logic
  const handleMenuNavigation = (e, path) => {
    // Prevent default Link behavior so your custom logic runs
    if (e) e.preventDefault();
    
    setIsMenuOpen(false);
    if (path === "/contact-us") {
      navigate("/loading", {
        state: { message: "Preparing Contact Page...", redirectTo: "/contact-us" }
      });
    } else if (path === "/services") {
      navigate("/");
      setTimeout(() => {
        const servicesSection = document.getElementById("our-services");
        if (servicesSection) servicesSection.scrollIntoView({ behavior: "smooth" });
      }, 0);
    } else {
      navigate(path);
    }
  };

  // --- STYLING HELPERS ---

  // UPDATED LOGO LOGIC
  const getLogoSrc = () => {
    if (location.pathname === "/loading") return "/logo-file-white.png";
    const isMobile = windowWidth < 768;
    if (location.pathname === "/contact-us") {
      if (!isScrolled && !isMobile) return "/framelync-white-trans.png";
    }
    return "/logo-file-black.png";
  };

  // UX: Dynamic Menu Button Styling
  const getMenuButtonClass = () => {
    const base = "flex items-center gap-2 px-5 py-2.5 rounded-full transition-all duration-300 backdrop-blur-md shadow-sm border";
    
    if (isMenuOpen) {
      return `${base} ${THEME.menuBg} text-white border-transparent ring-2 ring-black/10`;
    }
    
    if (location.pathname === "/loading") {
      return `${base} bg-white/10 text-white border-white/20 hover:bg-white/20`;
    }

    return `${base} ${isScrolled ? "bg-white/80 border-black/5" : "bg-white/60 border-transparent"} ${THEME.textMain} hover:bg-[#0F0F0F] hover:text-white hover:border-transparent`;
  };

  // SEO FIX: Changed <button> to <Link>
  // This allows Google to see the HREF, but we interrupt the click to keep your animation logic.
  const MenuItem = ({ path, label }) => {
    const isActive = location.pathname === path;
    return (
      <Link
        to={path}
        onClick={(e) => handleMenuNavigation(e, path)}
        className={`group relative w-full text-left py-4 px-6 transition-all duration-300 flex items-center justify-between border-b border-white/10 last:border-0 ${
          isActive ? "bg-white/10 text-white" : "text-gray-400 hover:text-white hover:bg-white/5"
        }`}
        aria-current={isActive ? "page" : undefined}
      >
        <span className="text-xs uppercase tracking-[0.2em] font-medium transition-transform duration-300 group-hover:translate-x-2">
          {label}
        </span>
        <ChevronRight size={14} className="opacity-0 -translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 text-white/50" />
      </Link>
    );
  };

  // Schema Markup for Navigation
  const navigationSchema = {
    "@context": "https://schema.org",
    "@type": "SiteNavigationElement",
    "name": ["Home", "Selected Works", "Contact"],
    "url": ["https://www.framelync.com/", "https://www.framelync.com/our-works", "https://www.framelync.com/contact-us"]
  };

  return (
    <>
    <Helmet>
        <script type="application/ld+json">
            {JSON.stringify(navigationSchema)}
        </script>
    </Helmet>

    <header 
      role="banner"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-700 ease-[0.22,1,0.36,1] ${
        isScrolled 
          ? `${THEME.bgScrolled} backdrop-blur-xl border-b border-black/5` 
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="relative flex items-center justify-between h-24 sm:h-28"> 
          
          {/* --- LOGO --- */}
          <Link
            to="/"
            className="relative z-50 block -ml-2 sm:ml-0"
            aria-label="FrameLync Agency Home" // Expanded aria-label
          >
             <motion.img
                key={getLogoSrc()} 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                src={getLogoSrc()}
                alt="FrameLync Agency Logo" // Descriptive Alt Text
                className={`w-auto object-contain transition-all duration-700 ease-[0.22,1,0.36,1] ${
                   isScrolled ? logoSizeScrolled : logoSizeDefault
                }`}
             />
          </Link>

          {/* --- MENU SYSTEM --- */}
          <nav 
            className="relative z-50" 
            ref={menuRef}
            role="navigation"
            aria-label="Main Navigation"
          >
            <button
              className={getMenuButtonClass()}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close Menu" : "Open Menu"}
              aria-expanded={isMenuOpen}
              aria-controls="main-menu-dropdown"
            >
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold hidden sm:block">
                {isMenuOpen ? "Close" : "Menu"}
              </span>
              <div className="relative w-4 h-4">
                 <AnimatePresence mode="wait">
                    {isMenuOpen ? (
                        <motion.div 
                            key="close"
                            initial={{ rotate: -90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: 90, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="absolute inset-0"
                        >
                            <X size={16} />
                        </motion.div>
                    ) : (
                        <motion.div 
                            key="menu"
                            initial={{ rotate: 90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: -90, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="absolute inset-0"
                        >
                            <Menu size={16} />
                        </motion.div>
                    )}
                 </AnimatePresence>
              </div>
            </button>

            {/* --- DROPDOWN --- */}
            <AnimatePresence>
              {isMenuOpen && (
                <motion.div
                  id="main-menu-dropdown"
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className={`absolute right-0 mt-4 w-64 origin-top-right rounded-2xl shadow-2xl overflow-hidden ${THEME.menuBg} ring-1 ring-white/10`}
                >
                  <div className="flex flex-col py-2">
                    <MenuItem path="/" label="Home" />
                    <MenuItem path="/our-works" label="Selected Works" />
                    <MenuItem path="/contact-us" label="Contact" />
                  </div>
                  
                  <div className="bg-white/5 px-6 py-3 text-[10px] text-gray-500 uppercase tracking-widest flex justify-between">
                    <span>Est. 2025</span>
                    <span>FRAMELYNC</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </nav>

        </div>
      </div>
    </header>
    </>
  );
};

export default Header;